import {
  Component,
  AfterViewInit,
  ElementRef,
  Input,
  OnChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { ChartComponent } from '../chart.component';
import { select, Selection, EnterElement } from 'd3-selection';
import { arc, Arc } from 'd3-shape';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { partition, hierarchy, HierarchyNode } from 'd3-hierarchy';

import { SunburstData } from './sunburst-chart-base.model';
import { D3Event } from '../charts.model';

@Component({
  selector: 'app-sunburst-chart-base',
  template: '',
})
export class SunburstChartBaseComponent
  extends ChartComponent
  implements OnChanges, AfterViewInit {
  @Input() data: SunburstData;
  @Input() colorRange: ReadonlyArray<string> = schemeCategory10;

  @Output() clickElement = new EventEmitter<
    D3Event<HierarchyNode<SunburstData>>
  >();
  @Output() mouseOverElement = new EventEmitter<
    D3Event<HierarchyNode<SunburstData>>
  >();
  @Output() mouseOutElement = new EventEmitter<
    D3Event<HierarchyNode<SunburstData>>
  >();

  private radius: number;
  private arc: Arc<any, any>;
  private animationDuration = 500;

  private root: HierarchyNode<SunburstData>;

  constructor(protected el: ElementRef) {
    super(el);
  }

  ngAfterViewInit() {
    this.drawChart();
  }

  ngOnChanges(): void {
    if (!this.data) {
      return;
    }

    this.drawChart();
  }

  drawChart() {
    this.updateChartDimentions();

    if (this.width && this.height && this.data) {
      this.radius = Math.min(this.width, this.height) / 2;
      this.initElements();
      this.drawSunburst();
    }
    setTimeout(() => {
      this.hoverFirstBiggestPath();
    }, this.animationDuration * 2);
  }

  private initElements() {
    const partitionLayout = partition().size([2 * Math.PI, this.radius]);
    this.root = hierarchy(this.data).sum((d) => d.value);

    partitionLayout(this.root);
    this.svg = select(this.chartElement)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height)
      .append('g')
      .attr(
        'transform',
        'translate(' + this.width / 2 + ',' + this.height / 2 + ')'
      )
      .attr('cursor', 'pointer');
  }

  private drawSunburst() {
    this.svg
      .selectAll('path')
      .data(this.root.descendants())
      .enter()
      .call(this.drawPath.bind(this));
  }

  private drawPath(
    arcSelection: Selection<EnterElement, HierarchyNode<SunburstData>, any, {}>
  ) {
    this.arc = arc()
      .startAngle((d: any) => d.x0)
      .endAngle((d: any) => d.x1)
      .innerRadius((d: any) => (d.depth === 1 ? d.y0 + 20 : d.y0 + 10))
      .outerRadius((d: any) => (d.depth === 1 ? d.y1 - 5 : d.y1 - 7))
      .padAngle(0.02);

    const path = arcSelection
      .append('path')
      .attr('display', (d) => (d.depth ? null : 'none'))
      .attr('d', this.arc)
      .attr('class', (d) => (d.depth ? 'path' : null))
      .style('fill', (d) => {
        return d.data.color;
      });

    path
      .on('click', (d: any, i: number, elements: SVGPathElement[]) => {
        this.clickElement.emit({ d, i, elements });
      })
      .on('mouseover', this.onMouseEnter.bind(this))
      .on('mouseout', this.onMouseOut.bind(this));
  }

  private onMouseEnter(data: any, i: number, elements: SVGPathElement[]) {
    if (data.value === 0) { return; }
    this.mouseOverElement.emit({ d: data, i, elements });
    const arcOver = arc()
      .startAngle((d: any) => d.x0)
      .endAngle((d: any) => d.x1)
      .innerRadius((d: any) => (d.depth === 1 ? d.y0 + 20 : d.y0 + 10))
      .outerRadius((d: any) => (d.depth === 1 ? d.y1 : d.y1));
    if (i !== 1) {
      select(elements[1]).transition().duration(500).attr('d', this.arc);
    }
    select(elements[i]).transition().duration(500).attr('d', arcOver);
  }

  private onMouseOut(d: any, i: number, elements: SVGPathElement[]) {
    this.mouseOutElement.emit({ d, i, elements });
    select(elements[i]).transition().duration(500).attr('d', this.arc);
    this.hoverFirstBiggestPath();
  }

  private hoverFirstBiggestPath() {
    const firstBiggestPath = this.chartElement.querySelector('.path');
    if (!firstBiggestPath) { return; }
    firstBiggestPath.dispatchEvent(new MouseEvent('mouseover'));
  }
}
